<template>
  <form class="form-group" @submit.prevent="handleSubmit" ref="formRef">
    <p style="margin-bottom: 1.5rem; color: var(--color-grey-dark);">
      Un code de validation a été envoyé au <strong>{{ phone }}</strong>
    </p>

    <label for="verificationCode">Code de validation *</label>
    <input v-model="localData.verificationCode" type="text" id="verificationCode" maxlength="6"
      placeholder="Entrez le code à 6 chiffres" autofocus required />

    <button type="button" class="btn btn-secondary" @click="resendCode" :disabled="isResending"
      style="margin-top: 0.5rem;">
      {{ isResending ? 'Envoi en cours...' : 'Renvoyer le code' }}
    </button>

    <InfoBox type="success" v-if="successMessage" :closable="false">
      <p>{{ successMessage }}</p>
    </InfoBox>
    <InfoBox type="error" v-if="error" :closable="false">
      {{ error }}
    </InfoBox>

    <!-- Messages d'info -->
    <InfoBox v-if="infoMessage" v-model:open="infoOpen" :type="infoType" @close="infoOpen = false; infoMessage = ''">
      {{ infoMessage }}
    </InfoBox>



  </form>
</template>


<script setup>
import { config } from '@/config/env.js'
import { ref, watch } from 'vue'
import { useToast } from 'vue-toast-notification'
import { storeToRefs } from 'pinia'
import { useLocaleStore } from '@/stores/localeStore'
import InfoBox from '@/components/InfoBox.vue'

const props = defineProps({
  formData: {
    type: Object,
    default: () => ({
      phone: '',
      verificationCode: '',
    })
  }
})

const emit = defineEmits(['submit', 'cancel'])

const formRef = ref(null)
const error = ref('')
const successMessage = ref('')
const isResending = ref(false)
const toast = useToast()
const localeStore = useLocaleStore()
const { countryIso } = storeToRefs(localeStore)

const phone = ref(props.formData?.phone || '')

const localData = ref({
  verificationCode: props.formData?.verificationCode || ''
})

watch(() => props.formData, (newFormData) => {
  if (newFormData) {
    phone.value = newFormData.phone || ''
    localData.value = { verificationCode: newFormData.verificationCode || '' }
  }
}, { deep: true })

async function resendCode() {
  isResending.value = true
  error.value = ''
  successMessage.value = ''

  try {
    const response = await fetch(config.apiUrl + '/user/resend-verification-code', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        phone: phone.value,
        countryIso: countryIso.value
      })
    })

    const data = await response.json().catch(() => ({}))

    if (!response.ok) {
      throw new Error(data.message || 'Erreur lors du renvoi du code')
    }

    successMessage.value = 'Code renvoyé avec succès !'
    toast.success('Code renvoyé avec succès !')
  } catch (err) {
    error.value = err.message
    toast.error('Impossible de renvoyer le code')
  } finally {
    isResending.value = false
  }
}

const handleSubmit = async () => {
  error.value = ''
  successMessage.value = ''

  if (!formRef.value?.checkValidity()) {
    formRef.value?.reportValidity()
    return
  }

  if (localData.value.verificationCode.length !== 6) {
    error.value = 'Le code de validation doit contenir 6 caractères.'
    toast.error('Le code de validation doit contenir 6 caractères.')
    return
  }

  try {
    const response = await fetch(config.apiUrl + '/user/validate', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({
        phone: phone.value,
        verificationCode: localData.value.verificationCode,
        countryIso: countryIso.value
      })
    })

    const data = await response.json().catch(() => ({}))

    if (!response.ok) {
      throw new Error(data.message || 'Erreur lors de la validation du compte')
    }

    toast.success('Compte validé avec succès ! Bienvenue !')
    emit('submit', { validated: true, user: data })
  } catch (err) {
    error.value = err.message
    toast.error(err.message)
  }
}

defineExpose({ handleSubmit })
</script>
<style scoped>
.btn.btn-secondary {
  margin-bottom: 1rem;
}
</style>
